import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { ArrowLeftIcon, PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { Employee } from '../data/databaseSchema';
import { type EmployeeLocationModelsDetail, employeeService } from '../services/employeeService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { EmployeeLocationModelFormDrawer } from './EmployeeLocationModelFormDrawer';

interface EmployeeLocationModelDrawerProps {
  employee: Employee | null;
  isOpen: boolean;
  onClose: () => void;
}

type SubPaneMode = 'empty' | 'view' | 'add' | 'edit';

function formatEntryDate(value: string | undefined | null): string {
  if (!value) return 'N/A';
  return formatDateDdMmYyyy(value);
}

export const EmployeeLocationModelDrawer: React.FC<EmployeeLocationModelDrawerProps> = ({
  employee,
  isOpen,
  onClose,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<EmployeeLocationModelsDetail | null>(null);
  const [subPaneMode, setSubPaneMode] = useState<SubPaneMode>('empty');
  const [editingEntry, setEditingEntry] = useState<EmployeeLocationModelsDetail | null>(null);
  const queryClient = useQueryClient();

  const {
    data: locationModel = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['employeeLocationModel', employee?.employee_id],
    queryFn: () => employeeService.getEmployeeLocationModel(employee?.employee_id || ''),
    enabled: isOpen && !!employee?.employee_id,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const shouldShowError = Boolean(error);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const clearRowSelection = useCallback(() => {
    gridApi?.deselectAll();
    setSelectedEntry(null);
  }, [gridApi]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<EmployeeLocationModelsDetail>) => {
      const selected = event.api.getSelectedRows();
      if (selected.length === 0) {
        setSelectedEntry(null);
        setSubPaneMode('empty');
        return;
      }
      setSelectedEntry(selected[0]);
      setSubPaneMode('view');
      setEditingEntry(null);
    },
    []
  );

  const handleRowClicked = useCallback((event: RowClickedEvent<EmployeeLocationModelsDetail>) => {
    if (!event.data) return;
    setSelectedEntry(event.data);
    setSubPaneMode('view');
    setEditingEntry(null);
  }, []);

  const handleDelete = useCallback(
    async (id: string | number) => {
      if (!employee?.employee_id) return;

      if (window.confirm('Are you sure you want to delete this location model?')) {
        try {
          await employeeService.deleteEmployeeLocationModel(employee.employee_id, id);
          queryClient.invalidateQueries({
            queryKey: ['employeeLocationModel', employee.employee_id],
          });
          queryClient.invalidateQueries({
            queryKey: ['employees'],
          });
          clearRowSelection();
          setSubPaneMode('empty');
        } catch {
          toast.error('Failed to delete location model. Please try again.');
        }
      }
    },
    [employee?.employee_id, queryClient, clearRowSelection]
  );

  const handleAddClose = useCallback(() => {
    setSubPaneMode('empty');
    setEditingEntry(null);
  }, []);

  const handleEditClose = useCallback(() => {
    setSubPaneMode('view');
    setEditingEntry(null);
  }, []);

  const handleFormSave = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['employeeLocationModel', employee?.employee_id],
    });
    await queryClient.invalidateQueries({ queryKey: ['employees'] });
    if (editingEntry) {
      setSubPaneMode('view');
      setEditingEntry(null);
    } else {
      clearRowSelection();
      setSubPaneMode('empty');
    }
  }, [employee?.employee_id, queryClient, editingEntry, clearRowSelection]);

  const isCurrent = useCallback((rowData: EmployeeLocationModelsDetail | undefined): boolean => {
    if (!rowData?.start_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(rowData.start_date);
    startDate.setHours(0, 0, 0, 0);

    if (rowData.no_end_date) {
      return today >= startDate;
    }

    if (!rowData.end_date) return false;
    const endDate = new Date(rowData.end_date);
    endDate.setHours(0, 0, 0, 0);
    return today >= startDate && today <= endDate;
  }, []);

  const columnDefs: ColDef<EmployeeLocationModelsDetail>[] = useMemo(
    () => [
      {
        headerName: 'Start Date',
        field: 'start_date',
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: { value?: string }) => formatEntryDate(params.value),
      },
      {
        headerName: 'End Date',
        field: 'end_date',
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: { value?: string }) => formatEntryDate(params.value),
      },
      {
        headerName: 'Location',
        field: 'location',
        flex: 1,
        minWidth: 200,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: ICellRendererParams<EmployeeLocationModelsDetail>) => {
          const location = params.data?.location;
          const isCurrentRow = isCurrent(params.data);

          return (
            <div className="flex flex-col h-full items-start gap-1">
              {isCurrentRow ? (
                <span className="rounded-sm bg-green-500 px-1 py-0.5 text-[8px] font-semibold text-white uppercase">
                  Current
                </span>
              ) : null}
              <div className="text-text-primary text-xs font-semibold">
                {location?.name || 'N/A'}
              </div>
            </div>
          );
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        cellRenderer: (params: { value?: string }) => {
          const status = params.value || 'active';
          const statusClass =
            status === 'active'
              ? 'text-success-600 bg-success-500/20'
              : 'text-text-secondary bg-muted';

          return (
            <span className={cn('rounded px-2 py-1 text-xs font-semibold capitalize', statusClass)}>
              {status}
            </span>
          );
        },
      },
    ],
    [isCurrent]
  );

  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    cellSelection: false,
    onGridReady: handleGridReady,
    onRowClicked: handleRowClicked,
    onSelectionChanged: handleSelectionChanged,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    rowNumbers: { minWidth: 25 },
    alternateBackground: true,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    rowHeight: 65,
  };

  const handleAddNewRow = () => {
    clearRowSelection();
    setEditingEntry(null);
    setSubPaneMode('add');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border-default px-4 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeftIcon data-icon="inline-start" />
          Back
        </Button>
        <h2 className="text-md font-semibold text-text-primary">Location Model</h2>
        <div className="flex items-center gap-2 rounded-sm px-2 shadow-sm">
          <span className="text-sm font-semibold text-text-primary">
            {employee?.fullname || 'Employee'}
          </span>
          <span className="rounded-sm px-1 text-xs text-text-secondary">
            {employee?.employee_id}
          </span>
        </div>
        <Button type="button" className="ml-auto" size="sm" onClick={handleAddNewRow}>
          <PlusIcon data-icon="inline-start" />
          Add Location Model
        </Button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-4">
          <style>{agGridThemeStyles}</style>
          <div
            className="ag-theme-material relative min-h-0 flex-1"
            style={agGridSettingsContainerStyles}
          >
            <AgGridReact
              ref={gridRef}
              rowData={locationModel}
              getRowId={(params) => String(params.data.id)}
              columnDefs={columnDefs}
              defaultColDef={agGridDefaultColDef}
              gridOptions={gridOptions}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
            />

            {isLoading ? (
              <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg bg-surface-card px-8 py-4 text-sm font-medium text-text-secondary shadow-md">
                <Spinner />
                Loading location models...
              </div>
            ) : null}

            {shouldShowError ? (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-danger-500/30 bg-danger-500/10 px-4 py-3 text-sm text-danger-600">
                Failed to load location models:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </div>
            ) : null}

            {!isLoading && !shouldShowError && locationModel.length === 0 ? (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-text-secondary">
                <div className="mb-2 text-3xl">🏢</div>
                <div>No location models found</div>
              </div>
            ) : null}
          </div>
        </div>

        <aside className="flex w-[300px] min-w-[300px] shrink-0 flex-col overflow-hidden border-l border-border-default bg-white">
          {subPaneMode === 'view' && selectedEntry ? (
            <EmployeeLocationModelFormDrawer
              key={selectedEntry.id}
              employee={employee}
              isOpen={true}
              readOnly
              entry={selectedEntry}
              onClose={() => {
                clearRowSelection();
                setSubPaneMode('empty');
              }}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingEntry(selectedEntry);
                setSubPaneMode('edit');
              }}
              onDeleteRequest={() => {
                void handleDelete(selectedEntry.id);
              }}
            />
          ) : subPaneMode === 'add' ? (
            <EmployeeLocationModelFormDrawer
              employee={employee}
              isOpen={true}
              onClose={handleAddClose}
              onSave={handleFormSave}
            />
          ) : subPaneMode === 'edit' && editingEntry ? (
            <EmployeeLocationModelFormDrawer
              employee={employee}
              isOpen={true}
              isEdit
              entry={editingEntry}
              onClose={handleEditClose}
              onSave={handleFormSave}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
              <p className="text-sm text-text-secondary">
                Select a row to view, or add a new model.
              </p>
              <Button type="button" onClick={handleAddNewRow}>
                <PlusIcon data-icon="inline-start" />
                Add Location Model
              </Button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};
